import { Extension } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export const searchHighlightPluginKey = new PluginKey("searchHighlight");

interface SearchHighlightState {
  decorations: DecorationSet;
  searchQuery: string;
  activeMatchIndex: number;
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildDecorations(
  doc: ProseMirrorNode,
  searchQuery: string,
  activeMatchIndex: number,
): DecorationSet {
  if (!searchQuery || !searchQuery.trim()) {
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];
  const escapedQuery = escapeRegExp(searchQuery.trim());
  const regex = new RegExp(escapedQuery, "gi");
  let matchCount = 0;

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;

    regex.lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(node.text)) !== null) {
      const from = pos + match.index;
      const to = from + match[0].length;
      const isActive = matchCount === activeMatchIndex;

      decorations.push(
        Decoration.inline(from, to, {
          class: isActive
            ? "search-highlight search-highlight--active"
            : "search-highlight",
        }),
      );
      matchCount++;
    }
  });

  return DecorationSet.create(doc, decorations);
}

/**
 * TipTap extension for search term highlighting using ProseMirror decorations.
 *
 * Decorations are purely visual — they don't modify the document model,
 * so they don't interfere with editing, undo/redo, copy/paste, or markdown serialization.
 *
 * Usage: Dispatch a transaction with metadata to update highlights:
 * ```ts
 * editor.view.dispatch(
 *   editor.state.tr.setMeta(searchHighlightPluginKey, {
 *     searchQuery: "term",
 *     activeMatchIndex: 0,
 *   })
 * );
 * ```
 */
export const SearchHighlightExtension = Extension.create({
  name: "searchHighlight",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: searchHighlightPluginKey,

        state: {
          init(): SearchHighlightState {
            return {
              decorations: DecorationSet.empty,
              searchQuery: "",
              activeMatchIndex: -1,
            };
          },

          apply(tr, prev, _oldState, newState): SearchHighlightState {
            const meta = tr.getMeta(searchHighlightPluginKey) as
              | { searchQuery: string; activeMatchIndex: number }
              | undefined;

            if (meta) {
              return {
                searchQuery: meta.searchQuery,
                activeMatchIndex: meta.activeMatchIndex,
                decorations: buildDecorations(
                  newState.doc,
                  meta.searchQuery,
                  meta.activeMatchIndex,
                ),
              };
            }

            if (tr.docChanged && prev.searchQuery) {
              return {
                ...prev,
                decorations: buildDecorations(
                  newState.doc,
                  prev.searchQuery,
                  prev.activeMatchIndex,
                ),
              };
            }

            return prev;
          },
        },

        props: {
          decorations(state) {
            const pluginState = searchHighlightPluginKey.getState(state) as
              | SearchHighlightState
              | undefined;
            return pluginState?.decorations ?? DecorationSet.empty;
          },
        },
      }),
    ];
  },
});
